<template>
  <div class="report-edit-filter">
    <h3 class="report-edit-filter__h3">
      Default Date *
    </h3>
    <div class="report-edit-filter__date">
      <div :class="{'report-edit-filter__date--active': selectedDefaultDate.type === 'range'}" style="grid-column: 1">
        <h4 class="report-edit-filter__h3">
          Date Range
        </h4>
        <form-label>from (days back in time)</form-label>
        <form-input
          :value="getDateValue('range', 'from')"
          type="number"
          data-cy="edit-report-date-days-from"
          :min="0"
          @input="handleDateInput($event, 'range', 'from')" />
        <form-label>to (days back in time)</form-label>
        <form-input
          :value="getDateValue('range', 'to')"
          type="number"
          data-cy="edit-report-date-days-to"
          :min="0"
          @input="handleDateInput($event, 'range', 'to')" />
      </div>
      <div :class="{'report-edit-filter__date--active': selectedDefaultDate.type === 'weeks'}" style="grid-column: 2">
        <h4 class="report-edit-filter__h3">
          Last number of weeks
        </h4>
        <form-label>Number of weeks back (Mon - Sun)</form-label>
        <form-input
          :value="getDateValue('weeks')"
          type="number"
          data-cy="edit-report-date-weeks"
          :min="1"
          @input="handleDateInput($event, 'weeks')" />
      </div>
      <div :class="{'report-edit-filter__date--active': selectedDefaultDate.type === 'months'}" style="grid-column: 3">
        <h4 class="report-edit-filter__h3">
          Last number of months
        </h4>
        <form-label>Number of months back</form-label>
        <form-input
          :value="getDateValue('months')"
          type="number"
          data-cy="edit-report-date-months"
          :min="1"
          @input="handleDateInput($event, 'months')" />
      </div>
      <div :class="{'report-edit-filter__date--active': selectedDefaultDate.type === 'years'}" style="grid-column: 4">
        <h4 class="report-edit-filter__h3">
          Last number of years
        </h4>
        <form-label>Number of years back (Jan - Dec)</form-label>
        <form-input
          :value="getDateValue('years')"
          type="number"
          data-cy="edit-report-date-years"
          :min="1"
          @input="handleDateInput($event, 'years')" />
      </div>
    </div>
    <h3 class="report-edit-filter__h3">
      Default Fields
    </h3>
    <div class="report-edit-filter__fields">
      <div style="grid-column: 1; grid-row: 1;">
        <form-label>Country</form-label>
        <country-multiselect
          :selected-keys="defaultFilter.Countries"
          placeholder="Countries"
          data-cy="edit-report-countries"
          @valuesSelected="onCountriesSelected" />
      </div>
      <div style="grid-column: 2; grid-row: 1;">
        <form-label>Customer</form-label>
        <customer-multiselect
          :selected-keys="defaultFilter.Customers"
          placeholder="Customers"
          data-cy="edit-report-customers"
          @valuesSelected="onCustomersSelected" />
      </div>
      <div style="grid-column: 1; grid-row: 2;">
        <form-label>Parcel Status</form-label>
        <form-filtered-multiselect
          v-model="defaultFilter.Parcel_Status"
          :options="constants.PARCEL_STATUS_OPTIONS"
          track-by="value"
          label="label"
          data-cy="edit-report-parcel-status"
          placeholder="Parcel Status" />
      </div>
      <div style="grid-column: 2; grid-row: 2;">
        <form-label>Service Level</form-label>
        <form-filtered-multiselect
          v-model="defaultFilter.Service_Levels"
          :options="serviceLevelOptions"
          data-cy="edit-report-service-level"
          track-by="value"
          label="label"
          placeholder="Service Level" />
      </div>
      <div style="grid-column: 1; grid-row: 3;">
        <form-label>Current Event</form-label>
        <form-filtered-multiselect
          v-model="defaultFilter.Current_Event_Infos"
          :options="eventInfoOptions"
          track-by="value"
          label="label"
          data-cy="edit-report-current-event"
          placeholder="Current Event" />
      </div>
      <div style="grid-column: 2; grid-row: 3;">
        <form-label>Provider</form-label>
        <provider-multiselect
          :selected-keys="defaultFilter.Providers"
          placeholder="Providers"
          data-cy="edit-report-provider"
          @valuesSelected="onProvidersSelected" />
      </div>
      <div style="grid-column: 1; grid-row: 4;">
        <form-label>Tag</form-label>
        <tag-multiselect
          :selected-keys="defaultFilter.Tags"
          placeholder="Tags"
          data-cy="edit-report-tag"
          @valuesSelected="onTagsSelected" />
      </div>
    </div>
    <h3 class="report-edit-filter__h3">
      Default Postal Code
    </h3>
    <div class="report-edit-filter__postal">
      <div>
        <form-label>Postal Code From</form-label>
        <form-input v-model="defaultFilter.From_Postal_Code" data-cy="edit-report-postal-code-from" />
      </div>
      <div>
        <form-label>Postal Code To</form-label>
        <form-input v-model="defaultFilter.To_Postal_Code" data-cy="edit-report-postal-code-to" />
      </div>
    </div>
    <h3 class="report-edit-filter__h3">
      Default Flow
    </h3>
    <div class="report-edit-filter__flow">
      <form-label>Show Flow</form-label>
      <form-toggle-switch
        v-model="defaultFilter.Show_Forward_Flow"
        data-cy="edit-report-flow"
        :pre-selected="true"
        text-unchecked="Return"
        text-checked="Forward" />
    </div>
  </div>
</template>

<script lang="ts">
import constants from '@/constants'
import { set, cloneDeep } from 'lodash'
import { mapGetters, mapMutations, mapState } from 'vuex'
import { Report } from '@/webApi/DataModels'
export default {
  data () {
    return {
      selectedDefaultDate: {},
      defaultFilter: {
        Countries: [],
        Current_Event_Infos: [],
        Customers: [],
        Providers: [],
        Tags: [],
        Parcel_Status: [],
        Service_Levels: [],
        Show_Forward_Flow: true,
        From_Postal_Code: '',
        To_Postal_Code: ''
      },
      constants
    }
  },
  computed: {
    ...mapGetters('serviceLevel', ['serviceLevelOptions']),
    ...mapState('eventInfo', ['eventInfoOptions']),
    ...mapState('report', ['currentEditReport'])
  },
  watch: {
    defaultFilter: {
      handler: function () {
        // update the report filter everytime the parcel filter changes
        this.setCurrentEditReport(set(cloneDeep(this.currentEditReport), 'Report_Filter', this.defaultFilter))
      },
      deep: true
    }
  },
  mounted () {
    // get the values out of the current report (when editing for example) and transform them into a usable format
    this.defaultFilter = cloneDeep(this.currentEditReport.Report_Filter)
    if (this.currentEditReport.Date_Range_From !== -1) {
      this.selectedDefaultDate = {
        type: 'range',
        from: this.currentEditReport.Date_Range_From,
        to: this.currentEditReport.Date_Range_To
      }
    } else if (this.currentEditReport.Last_Months !== -1) {
      this.selectedDefaultDate = {
        type: 'months',
        value: this.currentEditReport.Last_Months
      }
    } else if (this.currentEditReport.Last_Weeks !== -1) {
      this.selectedDefaultDate = {
        type: 'weeks',
        value: this.currentEditReport.Last_Weeks
      }
    } else if (this.currentEditReport.Last_Years !== -1) {
      this.selectedDefaultDate = {
        type: 'years',
        value: this.currentEditReport.Last_Years
      }
    }
  },
  methods: {
    ...mapMutations('report', ['setCurrentEditReport']),
    onCountriesSelected (countries) {
      this.defaultFilter.Countries = countries
    },
    onCustomersSelected (customers) {
      this.defaultFilter.Customers = customers
    },
    onProvidersSelected (providers) {
      this.defaultFilter.Providers = providers
    },
    onTagsSelected (tags) {
      this.defaultFilter.Tags = tags
    },
    onStatusSelected (parcelStataus) {
      this.defaultFilter.Parcel_Status = parcelStataus
    },
    onServiceLevelSelected (serviceLevel) {
      this.defaultFilter.Service_Levels = serviceLevel
    },
    onEventSelected (event) {
      this.defaultFilter.Current_Event_Infos = event
    },
    // Handles input changes in the date fiels,
    // only one selection is possible so it
    // resets the selected date object if another
    // type is selected
    handleDateInput (value, type, specifier) {
      if (value === null) {
        return
      }
      if (this.selectedDefaultDate.type !== type) {
        this.selectedDefaultDate = {}
      }
      this.selectedDefaultDate.type = type
      if (specifier !== undefined) {
        this.selectedDefaultDate[specifier] = value
      } else {
        this.selectedDefaultDate.value = value
      }
      this.writeDateFilter()
      this.$forceUpdate()
    },
    // Gets the selected date value, in order to render the right value from the object that is selected
    getDateValue (type, specifier) {
      if (this.selectedDefaultDate.type === type) {
        if (specifier) {
          return this.selectedDefaultDate[specifier]
        } else {
          return this.selectedDefaultDate.value
        }
      }
      return null
    },
    writeDateFilter () {
      // Everytime the date filter is changed write it into a usable format for storing
      var report:Report = cloneDeep(this.currentEditReport) as Report
      report.Date_Range_From = -1
      report.Date_Range_To = -1
      report.Last_Months = -1
      report.Last_Weeks = -1
      report.Last_Years = -1
      switch (this.selectedDefaultDate.type) {
        case 'range':
          report.Date_Range_From = parseInt(this.selectedDefaultDate.from)
          report.Date_Range_To = parseInt(this.selectedDefaultDate.to)
          break
        case 'weeks':
          report.Last_Weeks = parseInt(this.selectedDefaultDate.value)
          break
        case 'months':
          report.Last_Months = parseInt(this.selectedDefaultDate.value)
          break
        case 'years':
          report.Last_Years = parseInt(this.selectedDefaultDate.value)
          break
      }
      this.setCurrentEditReport(report)
    }
  }
}
</script>

<style lang="scss">
  @import '../../../variables.scss';
  .report-edit-filter {
    margin-bottom: 100px;
    &__date {
      display: grid;
      grid-template-columns: 24% 24% 24% 24%;
      grid-gap: 7px;
      width: 100%;

      &--active {
        background-color: $grey_80 !important;
      }

      div {
        background-color: $grey_95;
        border-radius: 5px;
        padding: 10px;
      }

      .spl-checkbox-group__item__label {
        margin-bottom: 10px;
      }
    }

    &__h3 {
        padding-top: 10px !important;
        padding-bottom: 10px !important;
    }

    &__fields, &__postal {
       display: grid;
      grid-template-columns: 49% 49%;
      grid-gap: 7px;
      width: 100%;
      div {
        margin-bottom: 10px;
      }
    }

    &__flow:last-child {
      padding-bottom: 100px;
    }
  }
</style>
